// src/components/ReviewApplication.js
import React from 'react';
import { FaClipboardCheck, FaEdit } from 'react-icons/fa';
import { motion } from 'framer-motion';

// Maps for displaying human-readable labels
const membershipTypeLabels = {
    professional: 'Professional Member',
    associate: 'Associate Member',
    international: 'International Member',
    student: 'Student Member',
    vendor: 'Vendor Member',
};

const educationLabels = {
    senior_high: 'Senior High School',
    diploma: 'Diploma',
    bachelor: "Bachelor's Degree (BSC)",
    msc: 'Master of Science (MSc)',
    ma: 'Master of Arts (MA)',
    med: 'Master of Education (MEd)',
    mph: 'Master of Public Health (MPH)',
    mba: 'Master of Business Administration (MBA)',
    mres: 'Master of Research (MRes)',
    other_master: "Other Master's Degree",
    phd: 'PhD/Doctorate',
    other: 'Other Professional Qualification',
};

const employmentLabels = {
    employed: 'Employed',
    self_employed: 'Self-Employed',
    unemployed: 'Unemployed',
    student: 'Student',
    retired: 'Retired',
};

const experienceLabels = {
    '0-1': '0–1 years',
    '2-5': '2–5 years',
    '6-10': '6–10 years',
    '10+': '10+ years',
};

const Section = ({ title, onEdit, children }) => (
    <div className="card border-0 shadow-sm mb-3" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="h6 fw-semibold mb-0" style={{ color: '#6B8E23' }}>{title}</h3>
                {onEdit && (
                    <button
                        type="button"
                        onClick={onEdit}
                        className="btn btn-sm btn-outline-success d-flex align-items-center"
                        style={{ fontSize: '0.8rem' }}
                    >
                        <FaEdit className="me-1" /> Edit
                    </button>
                )}
            </div>
            {children}
        </div>
    </div>
);

const Field = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
        <div className="row mb-2">
            <div className="col-sm-4">
                <small className="text-muted fw-medium">{label}</small>
            </div>
            <div className="col-sm-8">
                <small className="fw-semibold">{value}</small>
            </div>
        </div>
    );
};

const ReviewApplication = ({ formData, onGoToStep, hasReviewed, setHasReviewed }) => {
    const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
    const isStudent = formData.membershipType === 'student';

    return (
        <motion.div {...fadeIn}>
            {/* Section Header */}
            <div className="d-flex align-items-center mb-4">
                <FaClipboardCheck className="me-3" style={{ fontSize: '2rem', color: '#6B8E23' }} />
                <h2 className="h3 fw-semibold mb-0" style={{ color: '#6B8E23' }}>Review Your Application</h2>
            </div>

            <div className="alert alert-info d-flex align-items-center mb-4" style={{ backgroundColor: '#e8f5e9', borderColor: '#6B8E23' }}>
                <i className="fas fa-info-circle me-2" style={{ color: '#6B8E23' }}></i>
                <small>Please review your information below before submitting. Click <strong>Edit</strong> on any section to make changes.</small>
            </div>

            {/* Membership Type */}
            <Section title="Membership Plan" onEdit={() => onGoToStep(0)}>
                <Field label="Membership Type" value={membershipTypeLabels[formData.membershipType] || formData.membershipType} />
                <Field label="Duration" value={formData.membershipDuration} />
                {formData.communication && formData.communication.length > 0 && (
                    <Field label="Communications" value={formData.communication.join(', ')} />
                )}
            </Section>

            {/* Personal Information */}
            <Section title="Personal Information" onEdit={() => onGoToStep(1)}>
                <Field label="Full Name" value={`${formData.title ? formData.title + ' ' : ''}${formData.firstName} ${formData.lastName}`} />
                <Field label="Email" value={formData.email} />
                <Field label="Phone" value={formData.phone} />
                <Field label="Date of Birth" value={formData.dateOfBirth} />
                <Field label="Gender" value={formData.gender ? formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1) : ''} />
                <Field
                    label="Address"
                    value={[formData.streetAddress, formData.city, formData.stateProvince, formData.postalCode, formData.country].filter(Boolean).join(', ')}
                />
            </Section>

            {/* Professional Details */}
            <Section title="Professional Details" onEdit={() => onGoToStep(2)}>
                {!isStudent && (
                    <>
                        <Field label="Employment Status" value={employmentLabels[formData.employmentStatus] || formData.employmentStatus} />
                        {formData.jobRole && <Field label="Job Role" value={formData.jobRole} />}
                    </>
                )}
                <Field label="Experience" value={experienceLabels[formData.experience] || formData.experience} />
                {formData.specialization && formData.specialization.length > 0 && (
                    <Field label="Specializations" value={formData.specialization.join(', ')} />
                )}
                {formData.license && <Field label="License" value={formData.license} />}
                {formData.governingBody && <Field label="Governing Body" value={formData.governingBody} />}
            </Section>

            {/* Educational Background */}
            <Section title="Educational Background" onEdit={() => onGoToStep(3)}>
                <Field label="Highest Education" value={educationLabels[formData.highestEducation] || formData.highestEducation} />
                <Field label="Institution" value={formData.institution} />
                {!isStudent && formData.graduationYear && (
                    <Field label="Graduation Year" value={formData.graduationYear} />
                )}
                {isStudent && formData.expectedCompletionDate && (
                    <Field label="Expected Completion" value={formData.expectedCompletionDate} />
                )}
                {formData.programAccreditation && (
                    <Field label="Program Accreditation" value={formData.programAccreditation === 'yes' ? 'Accredited' : 'Not Accredited'} />
                )}
                {formData.certifications && <Field label="Certifications" value={formData.certifications} />}
            </Section>

            {/* Confirmation checkbox */}
            <div className="card border-0 shadow-sm mt-4 mb-3" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                <div className="card-body p-4">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="confirmReview"
                            checked={hasReviewed}
                            onChange={(e) => setHasReviewed(e.target.checked)}
                            style={{ borderColor: '#6B8E23', width: '1.2em', height: '1.2em' }}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="confirmReview" style={{ fontSize: '0.95rem' }}>
                            I have carefully reviewed all the information above and confirm that it is accurate and complete.
                        </label>
                    </div>
                </div>
            </div>

            {/* Final notice */}
            <div className="alert alert-warning mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Before submitting:</strong> Please ensure all information is accurate. After submission, you will receive an email to set your password and activate your account.
            </div>
        </motion.div>
    );
};

export default ReviewApplication;
